import React, { useState } from "react";
import styled from "styled-components";
import { generateAgreementDocx } from "./generateAgreementDocx";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  background: #1410df;
  color: white;
  padding: 24px 28px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`;

const ModalBody = styled.div`
  padding: 28px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1f2937;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1410df;
    box-shadow: 0 0 0 3px rgba(20, 16, 223, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  color: #1f2937;
  background: white;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #1410df;
    box-shadow: 0 0 0 3px rgba(20, 16, 223, 0.1);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media screen and (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const DownloadBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #1410df;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover { background: #100dbf; }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 20px 0;
`;

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #1410df;
  margin-bottom: 12px;
`;

const initialForm = {
  razonSocial: "",
  ruc: "",
  nombreComercial: "",
  direccion: "",
  modalidad: "porcentual",
  precioMayorista: "",
  cuentaBancaria: "",
  horario: "",
  fechaInicio: "",
};

const DownloadModal = ({ onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isValid =
    form.razonSocial.trim() &&
    form.ruc.trim() &&
    form.nombreComercial.trim() &&
    form.direccion.trim() &&
    form.cuentaBancaria.trim() &&
    form.horario.trim() &&
    form.fechaInicio.trim();

  const handleDownload = async () => {
    if (!isValid) return;
    setLoading(true);
    try {
      await generateAgreementDocx(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Generar Acuerdo Comercial</ModalTitle>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </ModalHeader>
        <ModalBody>
          <SectionLabel>Datos del Restaurante</SectionLabel>

          <FormGroup>
            <Label>Razón Social</Label>
            <Input name="razonSocial" value={form.razonSocial} onChange={handleChange} placeholder="Ej: RESTAURANTE SABORES S.A.C." />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>RUC</Label>
              <Input name="ruc" value={form.ruc} onChange={handleChange} placeholder="20XXXXXXXXX" maxLength={11} />
            </FormGroup>
            <FormGroup>
              <Label>Nombre Comercial</Label>
              <Input name="nombreComercial" value={form.nombreComercial} onChange={handleChange} placeholder="Ej: Sabores Criollos" />
            </FormGroup>
          </Row>

          <FormGroup>
            <Label>Dirección del Establecimiento</Label>
            <Input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Ej: Av. Arequipa 1234, Lince, Lima" />
          </FormGroup>

          <Divider />
          <SectionLabel>Condiciones Comerciales</SectionLabel>

          <FormGroup>
            <Label>Modalidad de Retribución</Label>
            <Select name="modalidad" value={form.modalidad} onChange={handleChange}>
              <option value="porcentual">Comisión Porcentual (20%)</option>
              <option value="mayorista">Compra al Por Mayor</option>
            </Select>
          </FormGroup>

          {form.modalidad === "mayorista" && (
            <FormGroup>
              <Label>Detalle de Precio Mayorista</Label>
              <Input name="precioMayorista" value={form.precioMayorista} onChange={handleChange} placeholder="Ej: Menú a S/.9.00 (PVP S/.11.00)" />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Cuenta Bancaria para Depósitos</Label>
            <Input name="cuentaBancaria" value={form.cuentaBancaria} onChange={handleChange} placeholder="Ej: BCP Cta. Ahorro 191-XXXXXXX-0-XX" />
          </FormGroup>

          <Row>
            <FormGroup>
              <Label>Horario de Operación</Label>
              <Input name="horario" value={form.horario} onChange={handleChange} placeholder="Ej: Lun-Sáb 11:00-21:00" />
            </FormGroup>
            <FormGroup>
              <Label>Fecha de Inicio</Label>
              <Input name="fechaInicio" type="date" value={form.fechaInicio} onChange={handleChange} />
            </FormGroup>
          </Row>

          <DownloadBtn onClick={handleDownload} disabled={!isValid || loading}>
            {loading ? "Generando..." : "Descargar Acuerdo (.docx)"}
          </DownloadBtn>
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default DownloadModal;
